import { Center, Grid, GridItem } from '@chakra-ui/react';
import { useHistory, useParams } from 'react-router-dom';

import { DashboardActionState } from 'api/CompeonReverseApi/customer/queryHooks/inquiries';
import { Asset } from 'components/Asset';
import MarkdownWrapper from 'components/MarkdownWrapper/MarkdownWrapper';
import paths from 'constants/paths';
import { UPLOAD_DOCUMENTS_BUTTON } from 'modules/Inquiry/Form/formFields';
import { InquiryType } from 'modules/Inquiry/Inquiry.type';
import { translations } from 'new/form/common/types';
import { useSelectedInquiryTypeSpecificValue } from 'shared/chooseSelectedInquiryTypeSpecificComponent';
import { ButtonComponent } from 'theme/components/Button';
import { HeadingComponent } from 'theme/components/Heading';
import ArrowRightIcon from 'theme/components/Icon/ArrowRightIcon';
import { ProgressSection } from 'theme/components/ProgressSection';
import { TextComponent } from 'theme/components/Text';
import { useTranslations } from 'utils/hooks/useTranslations';

import { ProgressSectionType } from './types';

type AllRequiredFilesUploadedProps = {
  isFirst: boolean;
  isLast: boolean;
  isCompleted: boolean;
  isPending: boolean;
  completedValue: number;
  progressIncrement: number;
  progressTotal: number;
  id: string;
  state: string;
};

export const FileUploadAction = ({
  isFirst,
  isLast,
  isCompleted,
  completedValue,
  progressIncrement,
  progressTotal,
  id,
  state,
}: AllRequiredFilesUploadedProps) => {
  const t = useTranslations();
  const {
    description: defaultDescription,
    title,
    dzbDescription,
    cbBankDescription,
    action: defaultAction,
    cbBankAction,
  } = translations.pages.inquiryDetails.dashboard.actions.requiredFilesUploaded;

  const description = useSelectedInquiryTypeSpecificValue({
    [InquiryType.dzb]: t(dzbDescription),
    [InquiryType.default]: t(defaultDescription),
    [InquiryType.cbBank]: t(cbBankDescription),
  });

  const action = useSelectedInquiryTypeSpecificValue({
    [InquiryType.default]: t(defaultAction),
    [InquiryType.cbBank]: t(cbBankAction),
  });

  const { id: inquiryId } = useParams<{ id: string }>();
  const history = useHistory();

  const submitAction = async () => {
    history.push(paths.customer.inquiryDetails.documentExchange.replace(':id', inquiryId));
  };

  const showUploadButton = state === DashboardActionState.ACTIVE;

  return (
    <ProgressSection
      isFirst={isFirst}
      isLast={isLast}
      isCompleted={isCompleted}
      completedValue={completedValue}
      progressIncrement={progressIncrement}
      progressTotal={progressTotal}
    >
      <Grid templateColumns={['repeat(1, 1fr)', null, '2fr 1fr']} gap={12}>
        <GridItem>
          <HeadingComponent as="h4" color="brand.default" id={id} mb={2} variant="secondary">
            {t(title)}
          </HeadingComponent>

          <TextComponent mb={6}>
            <MarkdownWrapper text={t(description)} />
          </TextComponent>

          {showUploadButton ? (
            <ButtonComponent
              data-testid={UPLOAD_DOCUMENTS_BUTTON}
              leftIcon={<ArrowRightIcon boxSize={6} display="block" />}
              onClick={submitAction}
              variant="primary"
            >
              {action}
            </ButtonComponent>
          ) : null}
        </GridItem>
        <GridItem>
          <Center height="100%">
            <Asset
              type="dashboard"
              value={ProgressSectionType.DOCUMENT_UPLOAD}
              htmlHeight="200"
              alt=""
            />
          </Center>
        </GridItem>
      </Grid>
    </ProgressSection>
  );
};
